import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../config/axios';
import AimantraLoading from '../AimantraLoading/AimantraLoading';

const S = {
    root: {
        width: '100%',
        height: '92vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: '12px',
        color: '#1a1a2e',
        overflow: 'hidden',
    },
    topbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0 12px',
        background: '#f6f8fa',
        borderBottom: '1px solid #d0d7de',
        minHeight: '44px',
        flexWrap: 'wrap',
    },
    topbarBrand: {
        fontSize: '15px',
        fontWeight: 700,
        color: '#185fa5',
        letterSpacing: '-0.3px',
        whiteSpace: 'nowrap',
    },
    statsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 12px',
        background: '#f6f8fa',
        borderBottom: '1px solid #d0d7de',
        flexWrap: 'wrap',
    },
    statCard: {
        flex: '0 0 150px',
        background: '#fff',
        border: '1px solid #d0d7de',
        borderRadius: '6px',
        padding: '8px 12px',
    },
    statLabel: {
        fontSize: '10.5px',
        fontWeight: 600,
        color: '#57606a',
        display: 'block',
        marginBottom: '2px',
    },
    statCount: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#185fa5',
        lineHeight: 1,
    },
    legendWrap: {
        marginLeft: 'auto',
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '10.5px',
        color: '#57606a',
        fontWeight: 500,
    },
    legendDot: (color) => ({
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        background: color,
        display: 'inline-block',
    }),
    tabbar: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 0,
        overflowX: 'auto',
        background: '#f6f8fa',
        borderBottom: '1px solid #d0d7de',
        minHeight: '42px',
    },
    tab: (active) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        padding: '0 18px',
        height: '40px',
        fontSize: '11.5px',
        fontWeight: active ? 700 : 500,
        color: active ? '#185fa5' : '#57606a',
        background: active ? 'rgba(24,95,165,0.06)' : 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid #185fa5' : '2px solid transparent',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s',
    }),
    tableScroll: {
        overflowX: 'auto',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: '380px',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        minWidth: '700px',
    },
    th: {
        padding: '7px 10px',
        textAlign: 'left',
        fontSize: '10.5px',
        fontWeight: 700,
        color: '#fff',
        background: '#185fa5',
        borderRight: '1px solid rgba(255,255,255,0.18)',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
    },
    td: {
        padding: '6px 10px',
        fontSize: '11px',
        color: '#1a1a2e',
        borderRight: '1px solid #e8ecf0',
        verticalAlign: 'middle',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
    row: (even) => ({
        borderBottom: '1px solid #e8ecf0',
        background: even ? '#f8fafd' : '#ffffff',
        transition: 'background 0.1s',
    }),
    link: (color) => ({
        color: color === 'green' ? '#1a7f37' : color === 'red' ? '#cf222e' : '#185fa5',
        textDecoration: 'none',
        fontWeight: 600,
        wordBreak: 'break-all',
    }),
    emptyRow: {
        textAlign: 'center',
        padding: '40px',
        color: '#57606a',
        fontSize: '12px',
        fontStyle: 'italic',
    },
    loaderWrap: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
        color: '#185fa5',
        fontSize: '12px',
        fontStyle: 'italic',
    },
    statusbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '5px 14px',
        background: '#f6f8fa',
        borderTop: '1px solid #d0d7de',
        fontSize: '10.5px',
        color: '#57606a',
        flexShrink: 0,
    },
};

function SearchTender() {
    const emp_code = sessionStorage.getItem('sitemanagement_emp_code');

    const [state, setState] = useState({
        websites: [],
        loading: true,
        tab: 'central',
    });

    // ✅ color based on open timing
    const getColorBasedOnTime = (lastOpened) => {
        const now = new Date();
        const openedDate = new Date(lastOpened);
        const diffHours = (now - openedDate) / (1000 * 60 * 60);
        if (diffHours <= 24) return 'green';
        else if (diffHours > 72) return 'red';
        else return 'black';
    };

    // ✅ Fetch both site list and open log
    const handleGetSitesAndLastOpenedData = async () => {
        setState((prev) => ({ ...prev, loading: true }));
        try {
            const [sitesRes, trackingRes] = await Promise.all([
                axios.get(`${BASE_URL}/siteproject/trackedurls/`),
                axios.get(`${BASE_URL}/siteproject/trackedURLOpenLogfilter/${emp_code}/`),
            ]);

            const documents = sitesRes.data;
            const trackingInfo = trackingRes.data;

            const mergedData = documents.map((doc) => {
                const match = trackingInfo.find((t) => t.tracked_url === doc.id);
                return {
                    ...doc,
                    last_opened_at: match?.last_opened_at || null,
                    obj_id: match?.id || null,
                };
            });

            setState((prev) => ({ ...prev, websites: mergedData }));
        } catch (error) {
            console.error('Failed to fetch sites or tracking info', error);
        } finally {
            setState((prev) => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        handleGetSitesAndLastOpenedData();
    }, []);

    // ✅ tab change
    const handleTabChange = (val) => {
        setState((prev) => ({ ...prev, tab: val }));
    };

    // ✅ Update open log when clicked
    const handleClickOnLink = async (id, openedId) => {
        try {
            let res;
            if (openedId) {
                res = await axios.put(`${BASE_URL}/siteproject/trackedURLOpenLog/${openedId}/`, {
                    tracked_url: id,
                    last_opened_by: emp_code,
                });
            } else {
                res = await axios.post(`${BASE_URL}/siteproject/trackedURLOpenLog/`, {
                    tracked_url: id,
                    last_opened_by: emp_code,
                });
            }

            const web = state.websites.find((web) => web.id === res.data.tracked_url);
            const updatedWebsites = [
                ...state.websites.filter((web) => web.id !== res.data.tracked_url),
                { ...web, last_opened_at: res.data.last_opened_at, obj_id: res.data.id },
            ];

            setState((prev) => ({ ...prev, websites: updatedWebsites }));
        } catch (err) {
            console.error(err);
        }
    };

    // ---------------------------------------------------
    // 🧠 Categorization logic
    // ---------------------------------------------------
    const centralKeywords = [
        'etenders.gov.in',
        'eprocure.gov.in',
        'defproc.gov.in',
        'tenderwizard.in',
        'rlda.indianrailways.gov.in',
        'ireps.gov.in',
        'nhai',
        'morth',
        'bro',
        'rlda',
        'ireps',
    ];

    const stateKeywords = [
        'bihar',
        'uttar',
        'maharashtra',
        'gujarat',
        'tamil',
        'karnataka',
        'rajasthan',
        'odisha',
        'kerala',
        'punjab',
        'madhya',
        'andhra',
        'telangana',
        'jharkhand',
        'haryana',
        'chhattisgarh',
        'west bengal',
        'assam',
        'goa',
        'manipur',
        'mizoram',
        'nagaland',
        'sikkim',
        'tripura',
        'meghalaya',
        'arunachal',
        'delhi',
        'himachal',
        'andaman',
        'wb',
        'mp',
        'j&k',
    ];

    const getCategory = (site) => {
        const url = site.url?.toLowerCase() || '';
        const name = site.name?.toLowerCase() || '';

        if (centralKeywords.some((key) => url.includes(key) || name.includes(key))) return 'central';
        const otherKeywords = ['others'];
        if (otherKeywords.some((key) => url.includes(key) || name.includes(key))) {
            return 'other';
        }
        return 'state';
    };

    const getStateName = (site) => {
        const url = site.url?.toLowerCase() || '';
        const name = site.name?.toLowerCase() || '';
        const found = stateKeywords.find((key) => url.includes(key) || name.includes(key));
        return found ? found.charAt(0).toUpperCase() + found.slice(1) : null;
    };

    // ✅ Filtered list based on tab
    const filteredSites = state.websites.filter((site) => {
        const category = getCategory(site);
        if (state.tab === 'central') return category === 'central';
        if (state.tab === 'state') return category === 'state';
        if (state.tab === 'other') return category === 'other';
        return true;
    });

    // ✅ Categorize opened stats (per tab)
    const categorizeByLastOpened = () => {
        const now = new Date();

        let result = {
            within24: 0,
            within72: 0,
            others: 0,
        };

        filteredSites.forEach((item) => {
            const openedDate = new Date(item.last_opened_at);
            if (isNaN(openedDate)) {
                result.others++;
                return;
            }
            const diffInHours = (now - openedDate) / (1000 * 60 * 60);
            if (diffInHours <= 72) result.within72++;
            else result.others++;
        });

        return result;
    };

    const stats = categorizeByLastOpened();

    const tabs = [
        { id: 'central', label: 'Central Sites' },
        { id: 'state', label: 'State Sites' },
        { id: 'other', label: 'Other Sites' },
    ];

    // ---------------------------------------------------
    // 🖼️ UI
    // ---------------------------------------------------
    return (
        <div style={S.root}>
            {/* Top bar */}
            <div style={S.topbar}>
                <span style={S.topbarBrand}>Search Tender</span>
            </div>

            {/* Summary Stats */}
            <div style={S.statsRow}>
                <div style={S.statCard}>
                    <span style={S.statLabel}>Opened In Last 3 Days</span>
                    <span style={S.statCount}>{stats.within72}</span>
                </div>
                <div style={S.statCard}>
                    <span style={S.statLabel}>Not Opened</span>
                    <span style={S.statCount}>{stats.others}</span>
                </div>
                <div style={S.legendWrap}>
                    <div style={S.legendItem}>
                        <span style={S.legendDot('green')} />
                        Opened in last 3 days
                    </div>
                    <div style={S.legendItem}>
                        <span style={S.legendDot('red')} />
                        Not opened
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div style={S.tabbar}>
                {tabs.map((t) => (
                    <button key={t.id} style={S.tab(state.tab === t.id)} onClick={() => handleTabChange(t.id)}>
                        {t.label}
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: state.tab === t.id ? '#185fa5' : '#eaf1fb',
                                color: state.tab === t.id ? '#fff' : '#185fa5',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 700,
                                minWidth: '18px',
                                height: '18px',
                                padding: '0 5px',
                            }}
                        >
                            {state.websites.filter((s) => getCategory(s) === t.id).length}
                        </span>
                    </button>
                ))}
            </div>

            {/* Table */}
            <div style={S.tableScroll}>
                <table style={S.table}>
                    <colgroup>
                        <col style={{ width: '220px' }} />
                        <col style={{ width: '320px' }} />
                        <col style={{ width: '160px' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={S.th}>Website Name</th>
                            <th style={S.th}>Website Link</th>
                            <th style={{ ...S.th, borderRight: 'none' }}>Last Opened</th>
                        </tr>
                    </thead>
                    <tbody>
                        {state.loading ? (
                            <tr>
                                {/* <div style={S.loaderWrap}>Loading sites…</div> */}
                                <td colSpan={3} style={S.emptyRow}>
                                    <AimantraLoading />
                                </td>
                            </tr>
                        ) : filteredSites.length === 0 ? (
                            <tr>
                                <td colSpan={3} style={S.emptyRow}>No sites found in this category.</td>
                            </tr>
                        ) : (
                            filteredSites.map((site, idx) => {
                                const color = getColorBasedOnTime(site.last_opened_at);
                                const stateName = getStateName(site);
                                const d = new Date(site.last_opened_at);
                                const lastOpenedText =
                                    isNaN(d.getTime()) || d.getFullYear() === 1970 ? 'N/A' : d.toLocaleString();
                                return (
                                    <tr
                                        key={site.id}
                                        style={S.row(idx % 2 === 0)}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f6ff')}
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.background = idx % 2 === 0 ? '#f8fafd' : '#ffffff')
                                        }
                                    >
                                        <td style={{ ...S.td, fontWeight: 600 }}>
                                            {site.name}
                                            {state.tab === 'state' && stateName ? ` (${stateName})` : ''}
                                        </td>
                                        <td style={S.td}>
                                            <a
                                                href={site.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={S.link(color)}
                                                onClick={() => handleClickOnLink(site.id, site.obj_id)}
                                            >
                                                {site.url}
                                            </a>
                                        </td>
                                        <td style={{ ...S.td, borderRight: 'none', fontSize: '10.5px' }}>
                                            {lastOpenedText}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Status bar */}
            <div style={S.statusbar}>
                <span>
                    Showing <strong style={{ color: '#185fa5' }}>{filteredSites.length}</strong> sites
                </span>
                <span style={{ opacity: 0.4 }}>|</span>
                <span>
                    Total tracked: <strong style={{ color: '#185fa5' }}>{state.websites.length}</strong>
                </span>
            </div>
        </div>
    );
}

export default SearchTender;